<template>
    <div class="exposure-container">
        <div class="exposure-card">
            <div class="exposure-header">
                <a :href="'/companies/' + company?.slug" target="_blank" class="exposure-company">
                    <img v-if="company" :src="company?.logo" class="exposure-logo" />
                    <div v-else class="exposure-logo-skeleton"></div>
                    <div>
                        <p class="exposure-company-name">{{ company?.name ?? 'Loading...' }}</p>
                        <p class="exposure-company-type">{{ company?.type?.label ?? 'Loading...' }}</p>
                    </div>
                </a>
                <div>
                    <div class="exposure-risk-badge">
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <circle cx="4.00002" cy="4" r="3" fill="#F04438" />
                        </svg>
                        <p class="exposure-risk-text">{{ company?.trust_score ?? 'Loading...' }}</p>
                    </div>
                </div>
            </div>
            <div class="exposure-dates">
                <div class="exposure-date-item">
                    <p class="exposure-date">{{ exposure?.created_at ? formatDate(exposure.created_at, 'MMM D, YYYY') : 'N/A' }}</p>
                    <p class="exposure-date-label">Detected</p>
                </div>
                <div class="exposure-date-item">
                    <p class="exposure-date">{{ (exposure?.in_progress_at) ? formatDate(exposure?.in_progress_at, 'MMM D, YYYY') : 'N/A' }}</p>
                    <p class="exposure-date-label">Request Sent</p>
                </div>
                <div class="exposure-date-item" v-if="exposure?.status === 'failed' || exposure?.status === 'closed'">
                    <p class="exposure-date">{{ exposure?.closed_at ? formatDate(exposure.closed_at, 'MMM D, YYYY') : 'N/A' }}</p>
                    <p class="exposure-date-label">Closed</p>
                </div>
                <div class="exposure-date-item" v-else>
                    <p class="exposure-date">{{ exposure?.next_action_at ? formatDate(exposure.next_action_at, 'MMM D, YYYY') : 'Pending' }}</p>
                    <p class="exposure-date-label">Next Action</p>
                </div>
            </div>
                <prime-button variant="secondary" v-if="!impersonating" class="p-btn-root_secondary" @click="openCompanyProfile"> 
                    Company Profile 
                </prime-button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useDateHelpers } from '~/composables/useDateHelpers';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import { usePersonStore } from '~/stores/person';

const sidebarStore = useExposureSidebarStore();
const { formatDate } = useDateHelpers();
const {impersonating} = storeToRefs(usePersonStore());

// Use store refs for reactive data
const { exposure, company } = storeToRefs(sidebarStore);

const openCompanyProfile = () => {
    window.open('/companies/' + company.value?.slug, '_blank');
};
</script>

<style lang="scss" scoped>
.exposure-container {
    @apply container border-b border-[#E4E4E7] p-6 ;
    background-image: url('/assets/images/png/main/person/exposure-sidebar/bg-image.png');
}

.exposure-card {
    @apply py-6 px-6 w-full h-auto flex flex-col gap-6 rounded border border-[#D5D7DA] bg-[#FFF] shadow-[0px_1px_3px_0px_rgba(10,13,18,0.10),0px_1px_2px_-1px_rgba(10,13,18,0.10)];
}

.exposure-header {
    @apply h-[50px] flex flex-row sm:flex-nowrap gap-2 justify-between items-start;
}

.exposure-company {
    @apply gap-[12px] flex flex-row;
}

.exposure-logo {
    @apply h-[48px] w-[48px];
    border-radius: 50%;
    background-color: #f0f0f0;
    border: 3px solid var(--Base-White, #fff);
    box-shadow: 0px 4px 6px -1px rgba(10, 13, 18, 0.1), 0px 2px 4px -2px rgba(10, 13, 18, 0.06);
}

.exposure-company-name {
    @apply text-blue-600 font-inter text-[16px] font-semibold leading-normal cursor-pointer;
}

.exposure-company-type {
    @apply text-[#52525B] font-inter text-[12px] font-normal leading-[18px];
}

.exposure-risk-badge {
    @apply inline-flex p-[2px_8px_2px_6px] items-center gap-[4px] rounded-[16px] border border-[#FECDCA] bg-[#FEF3F2];
}

.exposure-risk-text {
    @apply text-[#B42318] text-center font-inter text-[12px] font-medium leading-[18px];
}

.exposure-dates {
    @apply flex sm:flex-row flex-col gap-3 justify-between items-center;
}

.exposure-date-item {
    @apply flex w-[145px] flex-col items-center gap-[4px];
}

.exposure-date {
    @apply text-[#09090B] text-center font-inter text-[14px] font-medium leading-[18px];
}

.exposure-date-label {
    @apply text-[#52525B] text-center font-inter text-[12px] font-normal leading-[18px];
}

.exposure-profile-btn {
    @apply w-full flex p-[8px_12px] text-[#414651] justify-center items-center gap-[4px] border-[1px] border-[#E9EAEB] rounded-[4px] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)];
    color: var(--Zinc-700, #3f3f46);
    border: 1px solid var(--Zinc-300, #d4d4d8);
    background: var(--Base-White, #fff);
}

.exposure-actions {
    @apply flex gap-3 w-full;
}
</style>
